
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Helper function to check if we're in development mode
const isDevelopment = import.meta.env.DEV || false;

const RazorpayScript = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  
  // Ensure Razorpay script is loaded
  useEffect(() => {
    // Check if script is already loaded
    if (window.Razorpay) {
      setIsLoaded(true);
      // Notify other components that Razorpay is ready
      window.dispatchEvent(new Event('razorpay_loaded'));
      return;
    }

    // In development mode, create a mock Razorpay object for testing
    if (isDevelopment && !window.Razorpay) {
      // Create a mock Razorpay constructor that can be used for testing
      window.Razorpay = function(options: any) {
        // Store the options for later use
        this.options = options;
        
        // Define the open method
        this.open = function() {
          // In development, automatically trigger the success handler after a short delay
          if (options.handler) {
            const mockEvent = {
              razorpay_payment_id: 'pay_mock_' + Math.random().toString(36).substring(2, 10),
              razorpay_order_id: options.order_id,
              razorpay_signature: 'mock_signature_' + Math.random().toString(36).substring(2, 15),
            };
            
            // Simulate a successful payment after 2 seconds
            setTimeout(() => {
              options.handler(mockEvent);
            }, 2000);
          }
        };
        
        return this;
      };
      
      // Static methods on Razorpay
      window.Razorpay.orders = {
        fetch: async (orderId: string) => {
          return { 
            id: orderId,
            status: 'created',
            amount: 1000
          };
        }
      };
      
      setIsLoaded(true);
      window.dispatchEvent(new Event('razorpay_loaded'));
      return;
    }

    const loadRazorpayScript = async () => {
      return new Promise<boolean>((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
          setIsLoaded(true);
          
          // Notify other components that Razorpay is ready
          window.dispatchEvent(new Event('razorpay_loaded'));
          resolve(true);
        };
        
        script.onerror = (error) => {
          setIsLoaded(false);
          
          // Increment load attempts for retry logic
          setLoadAttempts(prev => prev + 1);
          
          if (loadAttempts > 2) {
            toast.error('Payment system failed to load. Please refresh the page or check your internet connection.');
          }
          resolve(false);
        };
        
        document.body.appendChild(script);
      });
    };

    // Listen for the razorpay_loaded event (in case another component loads it)
    const handleRazorpayLoaded = () => {
      setIsLoaded(true);
    };
    
    window.addEventListener('razorpay_loaded', handleRazorpayLoaded);
    
    // Try loading the script
    loadRazorpayScript();
    
    // More aggressive retry mechanism
    const retryInterval = setInterval(() => {
      if (!window.Razorpay) {
        loadRazorpayScript();
      } else {
        clearInterval(retryInterval);
      }
    }, Math.min(3000 + (loadAttempts * 1000), 10000)); // Exponential backoff with cap
    
    return () => {
      window.removeEventListener('razorpay_loaded', handleRazorpayLoaded);
      clearInterval(retryInterval);
    };
  }, [loadAttempts]);

  return null;
};

export default RazorpayScript;
